import { buildVendorComponents, computeProcessingPercent, computeTransportPercent, findFirstScheduledIndex } from './financials';
import { postInvoicedVendorCreditsIdem, postPaidCreditsIdem, postPartialCreditsIdem, postCancellationReversalsIdem } from './postings';
import { isServiceActiveForMonth } from './serviceHelpers';

// Allowed transitions central definition
export const allowedMilestoneTransitions = {
  Scheduled: ['Invoiced', 'Skip'],
  Invoiced: ['Paid', 'Partial', 'Cancelled'],
  Paid: ['Cancelled'],
  Partial: ['Cancelled'],
  Cancelled: [],
  Skip: []
};

// Transition orchestrator used by AR/Contracts
export const applyMilestoneTransition = async ({
  contract,
  milestoneId,
  nextStatus,
  entities,
  financeConfig,
  createdBy,
  getEffectiveClientAvailable, // function(clientId) => number
  persistUpdate, // async(updatedContract) => void
}) => {
  const ct = contract || {};
  const logs = Array.isArray(ct.milestoneLogs) ? ct.milestoneLogs.map((m) => ({ ...m })) : [];
  const idx = logs.findIndex((m) => m && m.id === milestoneId);
  if (idx < 0) throw new Error('Milestone not found');
  const dbg = (...args) => { try { console.debug('[Milestones][Paid]', ...args); } catch (_) {} };

  // Transport fee = invoice total × transport percent (from financeConfig based on payment method)
  const computeTransportForMilestone = (invoiceTotal) => {
    try {
      const transportPct = computeTransportPercent({ method: ct?.defaultPaymentMethod, financeConfig });
      const transport = (invoiceTotal || 0) * (transportPct / 100);
      const rounded = Math.round(transport * 100) / 100;
      console.log('[milestones] Transport calculation:', { 
        invoiceTotal, 
        paymentMethod: ct?.defaultPaymentMethod,
        transportPercent: transportPct, 
        transport: rounded 
      });
      return rounded;
    } catch (e) {
      console.error('[milestones] computeTransportForMilestone error:', e);
      return 0;
    }
  };

  const computeInternalBaseForMilestone = () => {
    try {
      const services = Array.isArray(ct.services) ? ct.services : [];
      const ym = String(logs[idx]?.expectedDate || '');
      const isMonthly = (freq) => String(freq || '').toLowerCase() === 'monthly';
      // ✅ FIX: Use shared isServiceActiveForMonth helper for consistency
      const sumFor = (arr) => arr.reduce((sum, s) => sum + ((Number(s?.internalUnitCost) || 0) * (Number(s?.quantity) || 0)), 0);
      
      // Monthly services active for this period
      const monthlyActive = services.filter((s) => isMonthly(s?.frequency) && isServiceActiveForMonth(s, ym));
      const monthlySum = sumFor(monthlyActive);
      
      // One-time services: only include if NOT already applied
      const oneTimeUnapplied = services.filter((s) => !isMonthly(s?.frequency) && !s?.oneTimeApplied);
      const oneTimeSum = sumFor(oneTimeUnapplied);
      
      const total = Math.round((monthlySum + oneTimeSum) * 100) / 100;
      
      console.log('[milestones] Internal base breakdown:', { 
        ym, 
        servicesCount: services.length,
        monthlyActiveCount: monthlyActive.length,
        monthlyActive: monthlyActive.map(s => ({ 
          name: s?.name, 
          internalUnitCost: s?.internalUnitCost, 
          quantity: s?.quantity,
          subtotal: (Number(s?.internalUnitCost) || 0) * (Number(s?.quantity) || 0)
        })),
        monthlySum,
        oneTimeUnappliedCount: oneTimeUnapplied.length,
        oneTimeUnapplied: oneTimeUnapplied.map(s => ({
          name: s?.name,
          internalUnitCost: s?.internalUnitCost,
          quantity: s?.quantity,
          oneTimeApplied: s?.oneTimeApplied,
          subtotal: (Number(s?.internalUnitCost) || 0) * (Number(s?.quantity) || 0)
        })),
        oneTimeSum,
        total 
      });
      return total;
    } catch (e) {
      console.error('[milestones] computeInternalBaseForMilestone error:', e);
      return 0;
    }
  };

  // Helper to get active services for this milestone
  // Uses oneTimeAppliedMilestoneId to match one-time services to the milestone they were invoiced on
  const getActiveServicesForMilestone = () => {
    const services = Array.isArray(ct.services) ? ct.services : [];
    const ym = String(logs[idx]?.expectedDate || '');
    const currentMilestoneId = logs[idx]?.id;
    
    const isMonthly = (freq) => {
      const f = String(freq || '').toLowerCase();
      return f === 'monthly' || f.includes('month');
    };
    // ✅ FIX: Use shared isServiceActiveForMonth helper for consistency
    
    // Monthly services: active for this YM
    const monthlyActive = services.filter((s) => isMonthly(s?.frequency) && isServiceActiveForMonth(s, ym));
    
    // Helper to find which milestone a service was likely invoiced on (for legacy data)
    const findInvoicedMilestoneForService = (svc) => {
      const addedAt = svc?.addedAt ? new Date(svc.addedAt) : null;
      
      // Find the first milestone that:
      // 1. Has been invoiced (Invoiced, Paid, or Partial status)
      // 2. Has expectedDate >= service addedAt (or no addedAt means it was there from start)
      for (let i = 0; i < logs.length; i++) {
        const m = logs[i];
        if (!m || m.status === 'Cancelled' || m.status === 'Skip' || m.status === 'Scheduled') continue;
        
        // If no addedAt, assume service was there from start - use first invoiced milestone
        if (!addedAt) return i;
        
        // Get milestone date
        const mDate = m.expectedDate ? new Date(m.expectedDate + '-01') : null;
        if (!mDate) continue;
        
        // If service was added before or during this milestone's month, this is the one
        if (addedAt <= mDate || addedAt.toISOString().slice(0, 7) <= m.expectedDate) {
          return i;
        }
      }
      // If no match found, use current milestone if it's been invoiced
      const currentStatus = logs[idx]?.status;
      if (currentStatus === 'Invoiced' || currentStatus === 'Paid' || currentStatus === 'Partial') {
        return idx;
      }
      return -1;
    };
    
    // One-time services: include if oneTimeAppliedMilestoneId matches this milestone
    // This handles one-time services added mid-contract
    const oneTimeActive = services.filter((s) => {
      if (isMonthly(s?.frequency)) return false;
      if (!s?.oneTimeApplied) return false; // Must have been invoiced
      
      // Match by stored milestone ID (preferred)
      if (s?.oneTimeAppliedMilestoneId) {
        return s.oneTimeAppliedMilestoneId === currentMilestoneId;
      }
      
      // Legacy: no milestone ID stored, find the likely milestone based on addedAt
      const likelyMilestoneIdx = findInvoicedMilestoneForService(s);
      return likelyMilestoneIdx === idx;
    });
    
    const allActive = [...monthlyActive, ...oneTimeActive];
    
    console.log('[milestones] Active services for milestone:', {
      ym,
      milestoneIdx: idx,
      currentMilestoneId,
      monthlyCount: monthlyActive.length,
      oneTimeCount: oneTimeActive.length,
      totalActive: allActive.length,
      monthlyServices: monthlyActive.map(s => s?.name),
      oneTimeServices: oneTimeActive.map(s => ({ 
        name: s?.name, 
        appliedMilestoneId: s?.oneTimeAppliedMilestoneId,
        addedAt: s?.addedAt 
      }))
    });
    
    return allActive;
  };

  // Compute margin per service (returns array of { serviceName, marginUsd })
  const computeServiceMarginsForMilestone = () => {
    try {
      const activeServices = getActiveServicesForMilestone();
      
      const computeServiceMargin = (s) => {
        const unitPrice = Number(s?.unitPrice) || 0;
        const internalCost = Number(s?.internalUnitCost) || 0;
        const quantity = Number(s?.quantity) || 1;
        const marginPerUnit = unitPrice - internalCost;
        return Math.round(marginPerUnit * quantity * 100) / 100;
      };
      
      const serviceMargins = activeServices.map(s => ({
        serviceName: s?.name || s?.serviceName || 'Service',
        frequency: s?.frequency,
        unitPrice: Number(s?.unitPrice) || 0,
        internalCost: Number(s?.internalUnitCost) || 0,
        quantity: Number(s?.quantity) || 1,
        marginUsd: computeServiceMargin(s)
      })).filter(sm => sm.marginUsd !== 0);
      
      console.log('[milestones] Service margins:', serviceMargins);
      return serviceMargins;
    } catch (e) {
      console.error('[milestones] computeServiceMarginsForMilestone error:', e);
      return [];
    }
  };

  // Compute services base = sum of (internalUnitCost × quantity) for active services
  // Commission is calculated on INTERNAL COST, not unit price (matches Contracts.js logic)
  const computeServicesBaseForMilestone = () => {
    try {
      const activeServices = getActiveServicesForMilestone();
      const base = activeServices.reduce((sum, s) => {
        // Use internalUnitCost (vendor cost) as the commission base, NOT unitPrice
        const internalCost = Number(s?.internalUnitCost) || 0;
        const quantity = Number(s?.quantity) || 1;
        return sum + (internalCost * quantity);
      }, 0);
      const rounded = Math.round(base * 100) / 100;
      console.log('[milestones] Services base (internalCost × qty for partner % calculation):', rounded);
      return rounded;
    } catch (e) {
      console.error('[milestones] computeServicesBaseForMilestone error:', e);
      return 0;
    }
  };
  const current = String(logs[idx].status || 'Scheduled');
  const allowed = allowedMilestoneTransitions[current] || [];
  if (current !== nextStatus && !allowed.includes(nextStatus)) {
    throw new Error('Invalid status transition');
  }

  // Scheduled -> Invoiced enrich amounts and post vendor AP
  if (nextStatus === 'Invoiced') {
    // ⚠️ CRITICAL: Ensure ALL required fields are set during Invoiced transition
    // These values MUST be deterministic and never recomputed later
    
    // Validate expectedCommission is present
    if (typeof logs[idx].expectedCommission !== 'number') {
      console.error('[milestones][Invoiced] ERROR - Missing expectedCommission at Invoiced transition:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        expectedDate: logs[idx].expectedDate,
        milestoneStatus: logs[idx].status,
        expectedCommission: logs[idx].expectedCommission,
        typeof: typeof logs[idx].expectedCommission,
        milestone: logs[idx],
        // Additional context for debugging
        allMilestoneStatuses: logs.map(m => ({ id: m?.id, status: m?.status, expectedDate: m?.expectedDate })),
        contractServices: (ct.services || []).map(s => ({
          name: s?.name,
          frequency: s?.frequency,
          oneTimeApplied: s?.oneTimeApplied,
          oneTimeAppliedMilestoneId: s?.oneTimeAppliedMilestoneId,
          addedAt: s?.addedAt
        })),
        partnerRoles: ct.partnerRoles
      });
      throw new Error('expectedCommission must be set before transitioning to Invoiced. This should be calculated during milestone creation.');
    }
    
    // Validate expectedRevenueBase is present
    if (typeof logs[idx].expectedRevenueBase !== 'number') {
      console.error('[milestones][Invoiced] ERROR - Missing expectedRevenueBase at Invoiced transition:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        expectedDate: logs[idx].expectedDate,
        milestoneStatus: logs[idx].status,
        expectedRevenueBase: logs[idx].expectedRevenueBase,
        typeof: typeof logs[idx].expectedRevenueBase,
        milestone: logs[idx],
        // Additional context for debugging
        allMilestoneStatuses: logs.map(m => ({ id: m?.id, status: m?.status, expectedDate: m?.expectedDate })),
        contractServices: (ct.services || []).map(s => ({
          name: s?.name,
          frequency: s?.frequency,
          oneTimeApplied: s?.oneTimeApplied,
          oneTimeAppliedMilestoneId: s?.oneTimeAppliedMilestoneId,
          addedAt: s?.addedAt
        })),
        partnerRoles: ct.partnerRoles
      });
      throw new Error('expectedRevenueBase must be set before transitioning to Invoiced. This should be calculated during milestone creation.');
    }
    
    // Ensure processing fee present
    const baseComponents = (
      Number(logs[idx].expectedRevenueBase || 0) +
      Number(logs[idx].expectedTransport || 0) +
      Number(logs[idx].expectedCommission || 0)
    );
    
    if (typeof logs[idx].expectedProcessingFee !== 'number' && baseComponents > 0) {
      const pct = computeProcessingPercent({ method: ct?.defaultPaymentMethod, financeConfig });
      const fee = baseComponents * (pct / 100);
      logs[idx].expectedProcessingFee = Number(fee.toFixed(2));
      
      // ⚠️ CRITICAL: Only recalculate expectedAmount if it's not already set
      // Milestone amounts are LOCKED at creation and should NEVER change during status transitions
      if (typeof logs[idx].expectedAmount !== 'number') {
        logs[idx].expectedAmount = Number((baseComponents + fee).toFixed(2));
        console.log('[milestones][Invoiced] Calculated expectedAmount (was missing):', {
          timestamp: new Date().toISOString(),
          contractId: ct.id,
          milestoneId: logs[idx].id,
          baseComponents,
          fee,
          expectedAmount: logs[idx].expectedAmount
        });
      } else {
        console.log('[milestones][Invoiced] Preserved existing expectedAmount (NOT recalculated):', {
          timestamp: new Date().toISOString(),
          contractId: ct.id,
          milestoneId: logs[idx].id,
          existingExpectedAmount: logs[idx].expectedAmount,
          baseComponents,
          calculatedFee: fee,
          preservedOriginalValue: true
        });
      }
      
      console.log('[milestones][Invoiced] Calculated expectedProcessingFee:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        baseComponents,
        processingPercent: pct,
        calculatedFee: fee,
        expectedProcessingFee: logs[idx].expectedProcessingFee,
        expectedAmount: logs[idx].expectedAmount
      });
    }
    
    // Final validation: ensure expectedProcessingFee is now set
    if (typeof logs[idx].expectedProcessingFee !== 'number') {
      console.error('[milestones][Invoiced] ERROR - expectedProcessingFee still not set after calculation:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        baseComponents,
        milestone: logs[idx]
      });
      // Set to 0 as fallback to prevent downstream errors
      logs[idx].expectedProcessingFee = 0;
    }
    
    console.log('[milestones][Invoiced] Final milestone values:', {
      timestamp: new Date().toISOString(),
      contractId: ct.id,
      milestoneId: logs[idx].id,
      expectedRevenueBase: logs[idx].expectedRevenueBase,
      expectedTransport: logs[idx].expectedTransport,
      expectedCommission: logs[idx].expectedCommission,
      expectedProcessingFee: logs[idx].expectedProcessingFee,
      expectedAmount: logs[idx].expectedAmount
    });

    const firstScheduledIdx = findFirstScheduledIndex(logs);
    const ym = String(logs[idx]?.expectedDate || '');
    const currentMilestoneId = logs[idx]?.id;
    
    // Explicitly determine which one-time services should be posted for THIS milestone
    // Include services where:
    // 1. oneTimeAppliedMilestoneId matches this milestone (already assigned), OR
    // 2. oneTimeApplied is false AND no milestone ID yet (new, unassigned - assign to current)
    const services = Array.isArray(ct.services) ? ct.services : [];
    const oneTimeServiceIdsToPost = services
      .filter((s) => {
        const isOneTime = !String(s?.frequency || '').toLowerCase().includes('month');
        if (!isOneTime) return false;
        // Include if explicitly marked for this milestone
        if (s?.oneTimeAppliedMilestoneId === currentMilestoneId) return true;
        // Include if not yet applied and no milestone assigned (new service)
        if (!s?.oneTimeApplied && !s?.oneTimeAppliedMilestoneId) return true;
        return false;
      })
      .map((s) => s.id || s.originalCostId || s.original_cost_id)
      .filter(Boolean);
    
    console.log('[milestones] One-time services to post:', {
      currentMilestoneId,
      oneTimeServiceIdsToPost,
      allOneTimeServices: services.filter(s => !String(s?.frequency || '').toLowerCase().includes('month')).map(s => ({
        id: s.id,
        name: s.name,
        oneTimeApplied: s.oneTimeApplied,
        oneTimeAppliedMilestoneId: s.oneTimeAppliedMilestoneId,
      })),
    });
    
    const vendorComponents = buildVendorComponents({
      services: ct.services || [],
      ym,
      firstScheduledIdx,
      currentIdx: idx,
      entities: entities || [],
      oneTimeServiceIdsToPost,
    });
    if (vendorComponents.length > 0) {
      const res = await postInvoicedVendorCreditsIdem({
        contractId: ct.id,
        milestone: logs[idx],
        vendorComponents,
        createdBy,
      });
      if (res?.tokenRef) {
        logs[idx].postingRefs = [...(Array.isArray(logs[idx].postingRefs) ? logs[idx].postingRefs : []), res.tokenRef];
      }
    }
    logs[idx].status = 'Invoiced';
  }

  // Paid -> post partner/company credits after validations
  if (nextStatus === 'Paid') {
    const expected = Number(logs[idx].expectedAmount || 0);
    const actual = Number(logs[idx].actualAmount || 0);
    const applied = Number(logs[idx].appliedAmount || 0);
    const base = actual > 0 ? Math.min(actual, expected) : expected;
    
    // 🔍 RACE CONDITION LOG: Track Paid transition validation
    console.log('[milestones][Paid] VALIDATION START:', {
      timestamp: new Date().toISOString(),
      contractId: ct.id,
      milestoneId: logs[idx].id,
      expectedDate: logs[idx].expectedDate,
      expected,
      actual,
      applied,
      base,
      appliedMinusBase: applied - base,
      roundedDiff: Math.round((applied - base) * 100),
      existingPostingRefs: logs[idx].postingRefs
    });
    
    // Equality check only for Paid; available coverage is enforced at Invoiced time
    if (Math.round((applied - base) * 100) !== 0) {
      console.error('[milestones][Paid] VALIDATION FAILED - Applied/Base mismatch:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        applied,
        base,
        difference: applied - base
      });
      throw new Error('Applied must equal invoice total to mark Paid');
    }
    if (!logs[idx].actualDate) logs[idx].actualDate = new Date().toISOString().split('T')[0];
    logs[idx].actualAmount = base;
    const roles = Array.isArray(ct.partnerRoles) ? ct.partnerRoles : [];

    // Compute posting components
    const serviceMargins = computeServiceMarginsForMilestone();
    const transportUsd = computeTransportForMilestone(base);
    
    // ⚠️ FIX: ALWAYS use milestone stored values - NEVER recompute during Paid transition
    // These values MUST be set during Invoiced transition to ensure deterministic calculations
    const rawExpectedCommission = logs[idx]?.expectedCommission;
    const rawExpectedProcessingFee = logs[idx]?.expectedProcessingFee;
    
    // Strict validation: these values MUST exist by the time we're marking Paid
    if (typeof rawExpectedCommission !== 'number') {
      console.error('[milestones][Paid] CRITICAL ERROR - Missing expectedCommission:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        expectedDate: logs[idx].expectedDate,
        milestoneStatus: logs[idx].status,
        rawExpectedCommission,
        typeof: typeof rawExpectedCommission,
        milestone: logs[idx],
        // Additional context for debugging
        allMilestoneStatuses: logs.map(m => ({ id: m?.id, status: m?.status, expectedDate: m?.expectedDate })),
        contractServices: (ct.services || []).map(s => ({
          name: s?.name,
          frequency: s?.frequency,
          oneTimeApplied: s?.oneTimeApplied,
          oneTimeAppliedMilestoneId: s?.oneTimeAppliedMilestoneId,
          addedAt: s?.addedAt
        })),
        partnerRoles: ct.partnerRoles
      });
      throw new Error('expectedCommission must be set before marking Paid. Transition to Invoiced first to calculate values.');
    }
    
    if (typeof rawExpectedProcessingFee !== 'number') {
      console.error('[milestones][Paid] CRITICAL ERROR - Missing expectedProcessingFee:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        expectedDate: logs[idx].expectedDate,
        rawExpectedProcessingFee,
        typeof: typeof rawExpectedProcessingFee,
        milestone: logs[idx]
      });
      throw new Error('expectedProcessingFee must be set before marking Paid. Transition to Invoiced first to calculate values.');
    }
    
    // Use the stored values directly - these are deterministic and were calculated at Invoiced time
    const partnerCommissionUsd = Number(rawExpectedCommission);
    const processingUsd = Number(rawExpectedProcessingFee);
    
    // Debug: log that we're using stored values (expected path)
    console.log('[milestones][Paid] Using stored milestone values (deterministic):', {
      milestoneId: logs[idx]?.id,
      expectedCommission: rawExpectedCommission,
      expectedProcessingFee: rawExpectedProcessingFee,
      partnerCommissionUsd,
      processingUsd,
      hasExpectedCommission: 'expectedCommission' in (logs[idx] || {}),
      hasExpectedProcessingFee: 'expectedProcessingFee' in (logs[idx] || {}),
    });
    
    console.log('[milestones] Paid compute:', { 
      milestoneId: logs[idx]?.id,
      expected,
      actual,
      invoiceTotal: base,
      serviceMargins,
      transportUsd,
      partnerCommissionUsd,
      processingUsd,
      rolesCount: roles.length,
    });

    // 🔍 RACE CONDITION LOG: Track ledger posting initiation
    const totalToPost = serviceMargins.reduce((sum, sm) => sum + (sm.marginUsd || 0), 0) + 
                        transportUsd + 
                        processingUsd + 
                        partnerCommissionUsd;
    console.log('[milestones][Paid] INITIATING LEDGER POST:', {
      timestamp: new Date().toISOString(),
      contractId: ct.id,
      milestoneId: logs[idx].id,
      lineAmountUsd: base,
      breakdown: {
        serviceMargins: serviceMargins.reduce((sum, sm) => sum + (sm.marginUsd || 0), 0),
        transportUsd,
        processingUsd,
        partnerCommissionUsd,
        total: totalToPost
      },
      expectedVsTotal: base,
      difference: base - totalToPost,
      serviceMarginDetails: serviceMargins
    });

    const res = await postPaidCreditsIdem({
      contractId: ct.id,
      milestone: logs[idx],
      lineAmountUsd: base,
      partnerCommissionUsd,
      processingUsd,
      serviceMargins,
      transportUsd,
      partnerRoles: roles,
      beneficiaryCompanyId: ct.beneficiaryCompanyId,
      createdBy,
    });
    
    console.log('[milestones][Paid] LEDGER POST RESULT:', {
      timestamp: new Date().toISOString(),
      contractId: ct.id,
      milestoneId: logs[idx].id,
      skipped: res?.skipped,
      tokenRef: res?.tokenRef,
      resultCount: Array.isArray(res?.result) ? res.result.length : 0
    });
    
    if (res?.tokenRef) {
      logs[idx].postingRefs = [...(Array.isArray(logs[idx].postingRefs) ? logs[idx].postingRefs : []), res.tokenRef];
    }
    logs[idx].clientBalanceTracked = base > 0;
    logs[idx].status = 'Paid';
  }

  if (nextStatus === 'Partial') {
    const expected = Number(logs[idx].expectedAmount || 0);
    const applied = Number(logs[idx].appliedAmount || 0);
    if (!(applied > 0 && applied < expected)) throw new Error('Partial requires 0 < applied < expected');
    const available = (typeof getEffectiveClientAvailable === 'function') ? Number(getEffectiveClientAvailable(ct.clientEntityId) || 0) : Infinity;
    if (available + 1e-6 < applied) throw new Error('Insufficient client available');
    if (!logs[idx].actualDate) logs[idx].actualDate = new Date().toISOString().split('T')[0];
    const roles = Array.isArray(ct.partnerRoles) ? ct.partnerRoles : [];
    
    // Compute posting components
    const partialServiceMargins = computeServiceMarginsForMilestone();
    const partialTransportUsd = computeTransportForMilestone(expected);
    
    // ⚠️ FIX: ALWAYS use milestone stored values - NEVER recompute during Partial transition
    // These values MUST be set during Invoiced transition to ensure deterministic calculations
    const rawExpectedCommissionPartial = logs[idx]?.expectedCommission;
    const rawExpectedProcessingFeePartial = logs[idx]?.expectedProcessingFee;
    
    // Strict validation: these values MUST exist by the time we're marking Partial
    if (typeof rawExpectedCommissionPartial !== 'number') {
      console.error('[milestones][Partial] CRITICAL ERROR - Missing expectedCommission:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        expectedDate: logs[idx].expectedDate,
        milestoneStatus: logs[idx].status,
        rawExpectedCommission: rawExpectedCommissionPartial,
        typeof: typeof rawExpectedCommissionPartial,
        milestone: logs[idx],
        // Additional context for debugging
        allMilestoneStatuses: logs.map(m => ({ id: m?.id, status: m?.status, expectedDate: m?.expectedDate })),
        contractServices: (ct.services || []).map(s => ({
          name: s?.name,
          frequency: s?.frequency,
          oneTimeApplied: s?.oneTimeApplied,
          oneTimeAppliedMilestoneId: s?.oneTimeAppliedMilestoneId,
          addedAt: s?.addedAt
        })),
        partnerRoles: ct.partnerRoles
      });
      throw new Error('expectedCommission must be set before marking Partial. Transition to Invoiced first to calculate values.');
    }
    
    if (typeof rawExpectedProcessingFeePartial !== 'number') {
      console.error('[milestones][Partial] CRITICAL ERROR - Missing expectedProcessingFee:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: logs[idx].id,
        expectedDate: logs[idx].expectedDate,
        rawExpectedProcessingFee: rawExpectedProcessingFeePartial,
        typeof: typeof rawExpectedProcessingFeePartial,
        milestone: logs[idx]
      });
      throw new Error('expectedProcessingFee must be set before marking Partial. Transition to Invoiced first to calculate values.');
    }
    
    // Use the stored values directly - these are deterministic and were calculated at Invoiced time
    const partnerCommissionUsd = Number(rawExpectedCommissionPartial);
    const processingUsd = Number(rawExpectedProcessingFeePartial);
    
    console.log('[milestones][Partial] Using stored milestone values (deterministic):', {
      milestoneId: logs[idx]?.id,
      expectedCommission: rawExpectedCommissionPartial,
      expectedProcessingFee: rawExpectedProcessingFeePartial,
      partnerCommissionUsd,
      processingUsd,
    });
    
    const res = await postPartialCreditsIdem({
      contractId: ct.id,
      milestone: logs[idx],
      appliedUsd: applied,
      totalUsd: expected,
      partnerRoles: roles,
      beneficiaryCompanyId: ct.beneficiaryCompanyId,
      createdBy,
      partnerCommissionUsd,
      processingUsd,
      serviceMargins: partialServiceMargins,
      transportUsd: partialTransportUsd,
    });
    if (res?.tokenRef) {
      logs[idx].postingRefs = [...(Array.isArray(logs[idx].postingRefs) ? logs[idx].postingRefs : []), res.tokenRef];
    }
    logs[idx].clientBalanceTracked = applied > 0;
    logs[idx].status = 'Partial';
  }

  if (nextStatus === 'Cancelled') {
    await postCancellationReversalsIdem({ milestoneId: logs[idx].id, createdBy });
    logs[idx].appliedAmount = 0;
    logs[idx].clientBalanceTracked = false;
    logs[idx].status = 'Cancelled';
  }

  if (nextStatus === 'Skip') {
    logs[idx].expectedAmount = 0;
    logs[idx].actualAmount = 0;
    logs[idx].appliedAmount = 0;
    logs[idx].clientBalanceTracked = false;
    logs[idx].invoiceId = '';
    logs[idx].invoiceUrl = '';
    logs[idx].status = 'Skip';
  }

  const updated = { ...ct, milestoneLogs: logs };
  
  // 🔍 RACE CONDITION LOG: Track milestone transition completion
  console.log('[milestones][applyMilestoneTransition] TRANSITION COMPLETE:', {
    timestamp: new Date().toISOString(),
    contractId: ct.id,
    milestoneId: logs[idx]?.id,
    fromStatus: current,
    toStatus: nextStatus,
    finalAppliedAmount: logs[idx]?.appliedAmount,
    finalActualAmount: logs[idx]?.actualAmount,
    finalExpectedAmount: logs[idx]?.expectedAmount,
    postingRefsCount: Array.isArray(logs[idx]?.postingRefs) ? logs[idx].postingRefs.length : 0,
    postingRefs: logs[idx]?.postingRefs
  });
  
  if (typeof persistUpdate === 'function') {
    console.log('[milestones][applyMilestoneTransition] PERSISTING TO FIRESTORE:', {
      timestamp: new Date().toISOString(),
      contractId: ct.id,
      milestoneId: logs[idx]?.id,
      status: nextStatus
    });
    await persistUpdate(updated);
    console.log('[milestones][applyMilestoneTransition] FIRESTORE PERSIST COMPLETE:', {
      timestamp: new Date().toISOString(),
      contractId: ct.id,
      milestoneId: logs[idx]?.id
    });
  }
  
  return updated;
};

export default { applyMilestoneTransition, allowedMilestoneTransitions };

